import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Well, ButtonGroup, Button, Overlay, Popover, ListGroup, ListGroupItem, Modal } from 'react-bootstrap';
import ActivitySummary from '../components/ActivitySummary.jsx';
import ActivityProgressDetails from '../components/ActivityProgressDetails.jsx';
import ActivityDetailsTabs from '../components/ActivityDetailsTabs.jsx';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import PhotoStrip, { normalizeDatasetImageSrc } from '../components/PhotoStrip.jsx';
import ImageModal from '../components/ImageModal.jsx';
import NoteModal from '../components/NoteModal.jsx';
import Utils from '../util/Utils.jsx';
import { trackPageView, trackEvent } from '../util/Analytics.jsx';
import html2canvas from 'html2canvas';

import { useStateValue } from '../context/state.jsx';
import { openAlert, openPrompt } from '../context/alert.js';
import { setProjectActivities } from '../context/datasets.js';
import { getDisplayOrderedActivityIds } from '../util/FilterLogic.jsx';

const ActivityModal = () => {
	const navigate = useNavigate();
	const { datasetId, activityId } = useParams();
	const flagButtonRef = useRef(null);
	const fileInputRef = useRef(null);
	const captureRef = useRef(null);

	const [modalImageId, setModalImageId] = useState(null);
	const [modalNoteObj, setModalNoteObj] = useState(null);
	const [unsavedChanges, setUnsavedChanges] = useState(false);
	const [showFlagChooser, setShowFlagChooser] = useState(false);
	const [activeTab, setActiveTab] = useState(1);
	const [, setAnImageLoaded] = useState(false);

	const [{
		datasets,
		projectActivities,
		projectActivitiesMap,
		isOnline,
		filteredActivities,
		filteredActivitiesMap,
		wbsData,
		projectViewType,
		projectGroupBy,
		projectGroupByActivityCode,
		projectActivityCodesTree
	}, dispatch] = useStateValue();

	useEffect(() => {
		trackPageView();
	}, []);

	const hasUnsavedChanges = () => {
		return unsavedChanges;
	};

	const handleDroppedFiles = files => {
		const allowedExt = {
			jpeg: true,
			jpg: true,
			png: true,
			gif: true,
			pdf: true
		};

		let accepted = 0,
			rejected = 0;

		const formData = new FormData();

		for (const file of files) {
			const filename = file.name || `paste-${Date.now()}.png`;
			const fileExt = filename.split('.').pop().toLowerCase();

			if (fileExt in allowedExt || (file.type || '').startsWith('image/')) {
				const uploadName = (fileExt in allowedExt)
					? filename
					: `${filename.replace(/\.[^.]*$/, '') || 'paste'}.${(file.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`;
				formData.append('file[]', file, uploadName);
				accepted++;
			} else {
				rejected++;
			}
		}

		if (accepted > 0) {
			uploadActivityFile(formData);
		}

		if (rejected > 0) {
			dispatch(openAlert('Error', `There were ${rejected} file(s) rejected because file type must be one of the following: ${Object.keys(allowedExt).join(', ')}.`));
		}
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		noClick: true,
		onDrop: handleDroppedFiles
	});

	const confirmDeleteFile = () => {
		dispatch(openAlert('Delete?', 'Are you sure you want to delete this file? This action cannot be undone.', [
			{
				bsStyle: 'danger',
				text: 'Yes, delete it.',
				action: () => {
					deleteFile(modalImageId);
					setModalImageId(null);
				}
			}, {
				text: 'No, keep it.',
				action: () => true
			}]
		));
	};

	const deleteFile = imageId => {
		trackEvent('activity', 'click', 'delete activity file');
		const postData = {
			dataset: datasetId,
			taskDocId: imageId,
			file: projectActivities[projectActivitiesMap[activityId]].images[imageId]
		};

		Utils.ajax('deleteFile', postData, dispatch, () => {
			const projectActivitiesTemp = [...projectActivities],
				activity = projectActivitiesTemp[projectActivitiesMap[activityId]];

			delete activity.images[imageId];
			dispatch(setProjectActivities(projectActivitiesTemp));
		});
	};

	const triggerFileInput = () => {
		if (!isOnline) {
			dispatch(openAlert('File Uploading', 'File uploading is not availble while offline. Keep your files/pictures and upload them when back online.'));
			return false;
		}

		fileInputRef.current.click();
	};

	const handleFileChange = (e) => {
		const allowedExt = {
			jpeg: true,
			jpg: true,
			png: true,
			gif: true,
			pdf: true
		};

		const file = e.target.files[0];
		const filename = file.name;
		const fileExt = filename.split('.').pop().toLowerCase();

		if (!(fileExt in allowedExt)) {
			dispatch(openAlert('Unsupported file', `SiteWalker does not support '${fileExt}' files. Please choose one of the following: ${Object.keys(allowedExt).join(', ')}.`));
		} else {
			createAndUploadForm(file);
		}
	};

	const createAndUploadForm = file => {
		const formData = new FormData();
		formData.append('file[]', file, file.name);
		uploadActivityFile(formData);
	};

	const uploadActivityFile = formData => {
		trackEvent('activity', 'click', 'upload activity file');
		formData.append('datetime', new Date().getTime());
		formData.append('dataset', datasetId);
		formData.append('taskid', activityId);
		Utils.ajax('uploadFile', formData, dispatch, result => {
			const projectActivitiesTemp = [...projectActivities],
				activity = projectActivitiesTemp[projectActivitiesMap[activityId]];
			if (Array.isArray(activity.images)) activity.images = {};

			for (const i in result.uploadedFiles) {
				const f = result.uploadedFiles[i];
				activity.images[f.taskDocId] = normalizeDatasetImageSrc(f.src);
			}

			activity.hasChanged = true;
			dispatch(setProjectActivities(projectActivitiesTemp));
		});
	};

	const shareActivity = () => {
		dispatch(openPrompt('Share this activity. Enter an email address.', email => {
			const trimmedEmail = (email || '').trim();
			if (!trimmedEmail) {
				dispatch(openAlert('Missing email', 'You must enter an email address to share the activity.'));
				return;
			}

			const runShare = screenshotBlob => {
				const formData = new FormData();
				formData.append('dataset', datasetId);
				formData.append('activityId', activityId);
				formData.append('email', trimmedEmail);
				if (screenshotBlob) {
					const type = screenshotBlob.type || 'image/jpeg';
					const ext = type.indexOf('png') >= 0 ? 'png' : 'jpg';
					formData.append('screenshot', screenshotBlob, `activity.${ext}`);
				}

				Utils.ajax(
					'shareActivity',
					formData,
					dispatch,
					() => {
						dispatch(openAlert(
							'Sharing activity succeeded',
							'The current activity has been shared with ' + trimmedEmail
						));
					}
				);
			};

			const component = captureRef.current;
			if (!component) {
				runShare(null);
				return;
			}

			html2canvas(component, {
				windowWidth: 550,
				scale: 0.7,
				useCORS: true,
				logging: false,
				backgroundColor: '#ffffff'
			})
				.then(canvas => {
					if (typeof canvas.toBlob === 'function') {
						canvas.toBlob(blob => {
							runShare(blob || null);
						}, 'image/jpeg', 0.72);
						return;
					}
					// Legacy fallback
					const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
					fetch(dataUrl)
						.then(res => res.blob())
						.then(blob => { runShare(blob); })
						.catch(() => { runShare(null); });
				})
				.catch(() => {
					// Still share the activity link even if screenshot capture fails.
					runShare(null);
				});
		},
		'',
		'Email address'));
	};

	const _setAnImageLoaded = () => {
		setAnImageLoaded(true);
	};

	const setActivityFlag = flag => {
		trackEvent('activity', 'click', 'set activity flag');

		const projectActivitiesTemp = [...projectActivities],
			activity = projectActivitiesTemp[projectActivitiesMap[activityId]];

		Utils.ajax(
			'setActivityFlag',
			{
				dataset: datasetId,
				activities: [activityId],
				flag
			},
			dispatch,
			() => {
				activity.flag = flag === '0' ? null : flag;
				activity.hasChanged = true;
				dispatch(setProjectActivities(projectActivitiesTemp));
			}
		);
	};

	const confirmNavigate = (action) => {
		if (unsavedChanges) {
			dispatch(openAlert('Leave?', 'You will lose unsaved changes if you leave now. Are you sure you want to continue?', [
				{
					bsStyle: 'danger',
					text: 'Yes, leave',
					action
				}, {
					text: 'No, stay',
					action: () => true
				}]
			));
		} else {
			action();
		}
	};

	const doGoBack = () => {
		confirmNavigate(() => {
			navigate(`/app/dataset/${datasetId}`);
		});
	};

	if (!datasetId || !projectActivities || !projectActivitiesMap) {
		return null;
	} else if (!(activityId in projectActivitiesMap)) {
		return (<div>This activity is not in the project. You can choose one from the list of activities <a href={`/app/dataset/${datasetId}`}>here</a>.</div>);
	}

	const datasetInfo = datasetId && datasets ? datasets[datasetId] : null,
		activity = projectActivities[projectActivitiesMap[activityId]],
		images = Array.isArray(activity.images) ? {} : activity.images,
		flagOptions = datasetInfo.filterOptions.flags,
		flagItems = flagOptions.map(flag => {
			return (
				<ListGroupItem key={flag.Code} className={`flag-${flag.Code}`} onClick={() => {
					setShowFlagChooser(false);
					setActivityFlag(flag.Code);
				}}>
					{flag.Color} <span className="glyphicon glyphicon-flag"></span>
				</ListGroupItem>
			);
		});

	const orderedActivityIds = getDisplayOrderedActivityIds({
		projectViewType,
		wbsData,
		projectActivityCodesTree,
		projectGroupBy,
		projectGroupByActivityCode,
		filteredActivities,
		filteredActivitiesMap
	});
	const currentNavIndex = orderedActivityIds.findIndex(
		id => String(id) === String(activityId)
	);
	const prevActivityId = currentNavIndex > 0
		? orderedActivityIds[currentNavIndex - 1]
		: null;
	const nextActivityId = currentNavIndex >= 0 && currentNavIndex < orderedActivityIds.length - 1
		? orderedActivityIds[currentNavIndex + 1]
		: null;

	const goToActivity = (targetActivityId) => {
		if (targetActivityId == null) {
			return;
		}
		confirmNavigate(() => {
			setUnsavedChanges(false);
			navigate(`/app/dataset/${datasetId}/activity/${targetActivityId}/`);
		});
	};

	return (
		<Modal
			show
			onHide={doGoBack}
			dialogClassName="activity-modal"
			size="xl">
			<Modal.Header>
				<div className="activityModalHeaderNav">
					<Button onClick={doGoBack}>
						<span className="glyphicon glyphicon-arrow-left leftGlyph"></span>
						Back
					</Button>
					<ButtonGroup className="activityModalListNav">
						<Button
							title="Previous activity"
							disabled={prevActivityId == null}
							onClick={() => { goToActivity(prevActivityId); }}>
							<span className="glyphicon glyphicon-chevron-up"></span>
						</Button>
						<Button
							title="Next activity"
							disabled={nextActivityId == null}
							onClick={() => { goToActivity(nextActivityId); }}>
							<span className="glyphicon glyphicon-chevron-down"></span>
						</Button>
					</ButtonGroup>
				</div>
				<Well className="activityModalHeaderSummary">
					<ActivitySummary activity={activity} datasetInfo={datasetInfo} />
				</Well>
			</Modal.Header>
			<Modal.Body>
				<div ref={captureRef} className="activityModalCapture">
					<div className="activityModalPhotosAndDetails">
						<Well className={Object.keys(images).length === 0 ? 'hideOnMobile' : ''}>
							<div
								{...getRootProps({
									className: classnames('dropZone', { active: isDragActive })
								})}>
								<input {...getInputProps()} />
								<PhotoStrip
									images={images}
									setModalImageId={setModalImageId}
									setAnImageLoaded={_setAnImageLoaded} />
							</div>
						</Well>

						<div className="activityExtraControls">
							<ButtonGroup>
								<Button
									ref={flagButtonRef}
									className={activity.flag ? `flag-${activity.flag}` : ''}
									active={!!activity.flag}
									onClick={() => { setShowFlagChooser(true); }}>
									Flag <span className="glyphicon glyphicon-flag"></span>
								</Button>
								<Overlay
									target={flagButtonRef.current}
									show={showFlagChooser}
									placement="right"
									rootClose={true}
									onHide={() => { setShowFlagChooser(false); }}>
									<Popover id="accessibility" className="flagChooserPopover" title="Flag chooser">
										<ListGroup>{flagItems}</ListGroup>
									</Popover>
								</Overlay>
								<Button onClick={shareActivity}>Share <span className="glyphicon glyphicon-share"></span></Button>
								<Button onClick={triggerFileInput}>Upload <span className="glyphicon glyphicon-camera"></span></Button>
							</ButtonGroup>
							<input
								type="file"
								accept="image/*,application/pdf"
								capture="environment"
								ref={fileInputRef}
								onChange={handleFileChange} />
						</div>

						<Well>
							<ActivityProgressDetails
								datasetId={datasetId}
								activity={activity}
								datasetInfo={datasetInfo}
								setUnsavedChanges={setUnsavedChanges}
								hasUnsavedChanges={hasUnsavedChanges} />
						</Well>

						<Well>
							<ActivityDetailsTabs
								activity={activity}
								datasetInfo={datasetInfo}
								projectActivities={projectActivities}
								projectActivitiesMap={projectActivitiesMap}
								setModalNoteObj={setModalNoteObj}
								activeTab={activeTab}
								setActiveTab={setActiveTab} />
						</Well>
					</div>
				</div>
				<ImageModal
					imageSrc={normalizeDatasetImageSrc(images[modalImageId])}
					deleteFile={confirmDeleteFile}
					setModalImageId={setModalImageId} />
				<NoteModal
					noteObj={modalNoteObj}
					setModalNoteObj={setModalNoteObj} />
			</Modal.Body>
		</Modal>
	);
};

export default ActivityModal;
